import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { siftApiClient } from '../../api/siftApiClient';
import type { ClientSavedView, ClientTeamViewSet } from '../../api/types';
import {
  PRIORITY_ORDER, TIER_ORDER, DEFAULT_SORT_DIR, DEFAULT_SORTS,
  countActiveMetricFilters, isSortKey,
  type PriorityKey, type TierKey, type SortEntry,
  type MetricFilters, type MetricRange, type MetricFilterKey,
} from './clientViewTokens';

/**
 * Saved Client View views — a named snapshot of the whole board setup: entry
 * filters, priority/tier chips, metric ranges AND the carousel sort.
 *
 * Three scopes, all server-side (durable + cross-device):
 *   - **My views** (personal) — private, stored in the user's `USERS.USER_CONFIG`.
 *   - **Team views** (RDS-1.1) — saved by a manager for their own districts; reps see
 *     their manager's, directors and admins see every team's.
 *   - **Shared views** (global) — visible to everyone on the Client View, stored
 *     in the shared `VIEW_CONFIGS` row. Only managers/directors/admins may edit them.
 */
export interface ClientViewSet {
  name: string;
  managers: string[];
  marketCenters: string[];
  rdsTerritories: string[];
  cseReps: string[];
  sellers: string[];
  priorities: PriorityKey[];
  tiers: TierKey[];
  metricFilters: MetricFilters;
  /** The carousel sort chain, in precedence order. */
  sorts: SortEntry[];
  /** Inspector ZIP or seller id, plus its radius (empty = no location mode). */
  inspector: string;
  radiusMiles: number;
}

/** The current board snapshot (everything but the name). */
export type ClientViewSelection = Omit<ClientViewSet, 'name'>;

/** Count the active filter groups in a selection (for the "N filters" summary). */
function selectionCount(s: ClientViewSelection): number {
  return (
    s.managers.length + s.marketCenters.length + s.rdsTerritories.length + s.cseReps.length +
    s.sellers.length +
    s.priorities.length + s.tiers.length + countActiveMetricFilters(s.metricFilters) +
    (s.inspector.trim() ? 1 : 0)
  );
}

/** Coerce an opaque API saved view into a ClientViewSet (missing groups → []). */
function toViewSet(v: ClientSavedView): ClientViewSet {
  const arr = (k: string): string[] => (Array.isArray(v[k]) ? (v[k] as unknown[]).map(String) : []);
  const metricFilters: MetricFilters = {};
  const raw = v.metricFilters;
  if (raw && typeof raw === 'object') {
    for (const [k, range] of Object.entries(raw as Record<string, unknown>)) {
      if (!range || typeof range !== 'object') continue;
      const r = range as Partial<MetricRange>;
      const min = typeof r.min === 'number' ? r.min : null;
      const max = typeof r.max === 'number' ? r.max : null;
      if (min !== null || max !== null) metricFilters[k as MetricFilterKey] = { min, max };
    }
  }
  // Sort fields removed since the view was saved are dropped, not sorted by.
  const sorts: SortEntry[] = (Array.isArray(v.sorts) ? v.sorts : [])
    .flatMap((entry) => {
      const e = entry as Partial<SortEntry> | null;
      if (!e || !isSortKey(e.key)) return [];
      return [{ key: e.key, dir: e.dir === 'asc' || e.dir === 'desc' ? e.dir : DEFAULT_SORT_DIR[e.key] }];
    });
  return {
    name: String(v.name ?? ''),
    managers: arr('managers'),
    marketCenters: arr('marketCenters'),
    rdsTerritories: arr('rdsTerritories'),
    cseReps: arr('cseReps'),
    sellers: arr('sellers'),
    priorities: arr('priorities').filter((p): p is PriorityKey => (PRIORITY_ORDER as string[]).includes(p)),
    tiers: arr('tiers').filter((t): t is TierKey => (TIER_ORDER as string[]).includes(t)),
    metricFilters,
    sorts: sorts.length > 0 ? sorts : DEFAULT_SORTS,
    inspector: typeof v.inspector === 'string' ? v.inspector : '',
    radiusMiles: typeof v.radiusMiles === 'number' ? v.radiusMiles : 50,
  };
}

/** Overwrite a same-named set (case-insensitive), then sort by name. */
function upsert(list: ClientViewSet[], next: ClientViewSet): ClientViewSet[] {
  const without = list.filter((s) => s.name.toLowerCase() !== next.name.toLowerCase());
  return [...without, next].sort((a, b) => a.name.localeCompare(b.name));
}

/** The option lists the board currently knows about, used to flag stale saved values.
 *  Sellers are absent on purpose: clients are found via server-side search now, so
 *  the valid set is all 139k and cannot be enumerated here to check against. */
export interface ClientViewAvailable {
  managers: string[];
  marketCenters: string[];
  rdsTerritories: string[];
  /** Absent until the CSE Rep options load; absent means "do not flag". */
  cseReps?: string[];
}

interface StaleGroup { label: string; values: string[]; }

const STALE_GROUPS: { key: keyof ClientViewAvailable; label: string }[] = [
  { key: 'managers', label: 'Manager' },
  { key: 'marketCenters', label: 'Market Center' },
  { key: 'rdsTerritories', label: 'RDS Territory' },
  { key: 'cseReps', label: 'CSE Rep' },
];

// Stale values are REPORTED, never dropped: an empty option list just means the
// board hasn't loaded, and for managers a missing value can mean the user's
// permission scope rather than data that no longer exists.
function staleGroups(s: ClientViewSet, available?: ClientViewAvailable): StaleGroup[] {
  if (!available) return [];
  return STALE_GROUPS.flatMap(({ key, label }) => {
    const options = available[key];
    if (!options || options.length === 0) return [];
    const missing = s[key].filter((v) => !options.includes(v));
    return missing.length > 0 ? [{ label, values: missing }] : [];
  });
}

function describeStale(groups: StaleGroup[]): string {
  return groups.map((g) => `${g.label}: ${g.values.join(', ')}`).join(' · ');
}

/** Re-label a view in place; a collision with another name absorbs that view. */
function rename(list: ClientViewSet[], from: string, to: string): ClientViewSet[] {
  const target = list.find((s) => s.name === from);
  if (!target) return list;
  return upsert(list.filter((s) => s.name !== from), { ...target, name: to });
}

/** 'personal', 'global', or `team:<owner email>`. */
type Scope = string;
type SaveTarget = 'personal' | 'team' | 'global';

interface TeamSet { owner: string; audience: string[]; views: ClientViewSet[]; editable: boolean }

const teamScope = (owner: string): Scope => `team:${owner}`;

interface ClientSavedViewsProps {
  current: ClientViewSelection;
  onApply: (set: ClientViewSet) => void;
  available?: ClientViewAvailable;
  /** Display name for a team set (e.g. the manager's name from the territory mapping). */
  teamLabel?: (team: { owner: string; audience: string[] }) => string;
}

export const ClientSavedViews: React.FC<ClientSavedViewsProps> = ({ current, onApply, available, teamLabel }) => {
  const [personal, setPersonal] = useState<ClientViewSet[]>([]);
  const [globalViews, setGlobalViews] = useState<ClientViewSet[]>([]);
  const [canEditGlobal, setCanEditGlobal] = useState(false);
  const [teams, setTeams] = useState<TeamSet[]>([]);
  const [canSaveTeam, setCanSaveTeam] = useState(false);
  const [teamOwner, setTeamOwner] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [target, setTarget] = useState<SaveTarget>('personal');
  const [renaming, setRenaming] = useState<{ scope: Scope; name: string } | null>(null);
  const [renameDraft, setRenameDraft] = useState('');
  const [staleNotice, setStaleNotice] = useState<{ name: string; groups: StaleGroup[] } | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await siftApiClient.getClientSavedViews();
        if (cancelled) return;
        setPersonal(resp.personal.map(toViewSet));
        setGlobalViews(resp.global.map(toViewSet));
        setCanEditGlobal(resp.can_edit_global);
        setTeams((resp.team || []).map((t: ClientTeamViewSet) => ({
          owner: t.owner, audience: t.audience || [], views: (t.views || []).map(toViewSet), editable: t.editable,
        })));
        setCanSaveTeam(Boolean(resp.can_save_team));
        setTeamOwner(resp.team_owner || null);
      } catch {
        /* offline / unauthorized — leave the menu empty, non-fatal */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!open) { setRenaming(null); return; }
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const currentCount = useMemo(() => selectionCount(current), [current]);
  const trimmedName = name.trim();
  const total = personal.length + globalViews.length + teams.reduce((n, t) => n + t.views.length, 0);

  // Apply a mutation to the freshest list, persist it write-through, and roll
  // back on failure. The functional updater avoids stale-closure races when
  // saves fire back-to-back.
  const mutatePersonal = useCallback((fn: (prev: ClientViewSet[]) => ClientViewSet[]) => {
    setPersonal((prev) => {
      const next = fn(prev);
      void (async () => {
        try {
          await siftApiClient.saveClientPersonalViews(next as unknown as ClientSavedView[]);
        } catch {
          setPersonal(prev);
        }
      })();
      return next;
    });
  }, []);

  const mutateGlobal = useCallback((fn: (prev: ClientViewSet[]) => ClientViewSet[]) => {
    setGlobalViews((prev) => {
      const next = fn(prev);
      void (async () => {
        try {
          await siftApiClient.saveClientGlobalViews(next as unknown as ClientSavedView[]);
        } catch {
          setGlobalViews(prev);
        }
      })();
      return next;
    });
  }, []);

  // One manager's team set; created on first save. Write-through with rollback like the others.
  const mutateTeam = useCallback((owner: string, fn: (prev: ClientViewSet[]) => ClientViewSet[]) => {
    setTeams((prevTeams) => {
      const existing = prevTeams.find((t) => t.owner === owner);
      const prevViews = existing?.views || [];
      const nextViews = fn(prevViews);
      const nextTeams = existing
        ? prevTeams.map((t) => (t.owner === owner ? { ...t, views: nextViews } : t))
        : [...prevTeams, { owner, audience: [], views: nextViews, editable: true }];
      void (async () => {
        try {
          const saved = await siftApiClient.saveClientTeamViews(
            nextViews as unknown as ClientSavedView[],
            owner === teamOwner ? undefined : owner,
          );
          setTeams((cur) => cur.map((t) => (t.owner === owner ? { ...t, audience: saved.audience || t.audience } : t)));
        } catch {
          setTeams(prevTeams);
        }
      })();
      return nextTeams;
    });
  }, [teamOwner]);

  const mutatorFor = useCallback((scope: Scope) => {
    if (scope === 'global') return mutateGlobal;
    if (scope.startsWith('team:')) {
      const owner = scope.slice('team:'.length);
      return (fn: (prev: ClientViewSet[]) => ClientViewSet[]) => mutateTeam(owner, fn);
    }
    return mutatePersonal;
  }, [mutateGlobal, mutatePersonal, mutateTeam]);

  const save = useCallback(() => {
    if (!trimmedName) return;
    const next: ClientViewSet = { name: trimmedName, ...current };
    if (target === 'global' && canEditGlobal) mutateGlobal((prev) => upsert(prev, next));
    else if (target === 'team' && canSaveTeam && teamOwner) mutateTeam(teamOwner, (prev) => upsert(prev, next));
    else mutatePersonal((prev) => upsert(prev, next));
    setName('');
  }, [trimmedName, current, target, canEditGlobal, canSaveTeam, teamOwner, mutateGlobal, mutateTeam, mutatePersonal]);

  // Overwrite an existing view's body with what is on the board right now,
  // keeping its name and scope. Without this, "editing" a view means retyping
  // its exact name to trigger the same-name overwrite.
  const commitRename = useCallback(() => {
    if (!renaming) return;
    const to = renameDraft.trim();
    if (to && to !== renaming.name) {
      mutatorFor(renaming.scope)((prev) => rename(prev, renaming.name, to));
    }
    setRenaming(null);
  }, [renaming, renameDraft, mutatorFor]);

  const renderRow = (s: ClientViewSet, scope: Scope, editable: boolean) => {
    const isRenaming = editable && renaming?.scope === scope && renaming.name === s.name;
    const onUpdate = (n: string) => mutatorFor(scope)((prev) => upsert(prev, { name: n, ...current }));
    const onDelete = (n: string) => mutatorFor(scope)((prev) => prev.filter((v) => v.name !== n));
    const stale = staleGroups(s, available);

    if (isRenaming) {
      return (
        <li key={s.name} className="cv-saved-view-row">
          <input
            type="text"
            className="cv-saved-view-rename-input"
            value={renameDraft}
            maxLength={60}
            autoFocus
            aria-label={`New name for ${s.name}`}
            onChange={(e) => setRenameDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitRename();
              if (e.key === 'Escape') setRenaming(null);
            }}
          />
          <button
            type="button"
            className="cv-saved-view-update"
            onClick={commitRename}
            disabled={!renameDraft.trim()}
            aria-label={`Save new name for ${s.name}`}
            title="Save name"
          >
            ✓
          </button>
          <button
            type="button"
            className="cv-saved-view-del"
            onClick={() => setRenaming(null)}
            aria-label={`Cancel renaming ${s.name}`}
            title="Cancel"
          >
            ✕
          </button>
        </li>
      );
    }

    return (
      <li key={s.name} className="cv-saved-view-row">
        <button
          type="button"
          className="cv-saved-view-apply"
          onClick={() => {
            onApply(s);
            setStaleNotice(stale.length > 0 ? { name: s.name, groups: stale } : null);
            // Prefill the name box so re-saving obviously overwrites this view.
            setName(s.name);
            setTarget(scope === 'global' ? 'global' : scope === teamScope(teamOwner || '') ? 'team' : 'personal');
            setOpen(false);
          }}
          aria-label={`Apply "${s.name}"`}
          title={`Apply "${s.name}" (${selectionCount(s)} filters, ${s.sorts.length} sort field${s.sorts.length === 1 ? '' : 's'})`}
        >
          {s.name}
          <span className="cv-saved-view-count">· {selectionCount(s)}</span>
        </button>
        {stale.length > 0 && (
          <span
            className="cv-saved-view-stale-flag"
            title={`Values no longer on the board — ${describeStale(stale)}`}
            aria-label={`${s.name} has filter values that no longer exist`}
          >
            ⚠
          </span>
        )}
        {editable && (
          <>
            <button
              type="button"
              className="cv-saved-view-update"
              onClick={() => onUpdate(s.name)}
              aria-label={`Update saved view ${s.name} with the current filters`}
              title={`Overwrite "${s.name}" with the filters + sort on the board now`}
            >
              ↻
            </button>
            <button
              type="button"
              className="cv-saved-view-rename"
              onClick={() => { setRenaming({ scope, name: s.name }); setRenameDraft(s.name); }}
              aria-label={`Rename saved view ${s.name}`}
              title="Rename"
            >
              ✎
            </button>
            <button
              type="button"
              className="cv-saved-view-del"
              onClick={() => onDelete(s.name)}
              aria-label={`Delete saved view ${s.name}`}
              title="Delete"
            >
              ✕
            </button>
          </>
        )}
      </li>
    );
  };

  return (
    <div className="cv-saved-views" ref={wrapRef}>
      <button
        type="button"
        className="cv-reset-btn"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        title="Save the current filters + sort, or apply a saved view"
      >
        Saved views{total ? ` (${total})` : ''} ▾
      </button>
      {staleNotice && (
        <div className="cv-saved-view-stale" role="status">
          <span>
            “{staleNotice.name}” uses{' '}
            {staleNotice.groups.reduce((n, g) => n + g.values.length, 0)} filter value
            {staleNotice.groups.reduce((n, g) => n + g.values.length, 0) === 1 ? '' : 's'}{' '}
            that are no longer on the board ({describeStale(staleNotice.groups)}). They are still
            applied, so results may be empty until you clear them.
          </span>
          <button
            type="button"
            className="cv-saved-view-stale-close"
            onClick={() => setStaleNotice(null)}
            aria-label="Dismiss"
            title="Dismiss"
          >
            ✕
          </button>
        </div>
      )}
      {open && (
        <div className="cv-saved-views-menu" role="listbox">
          <div className="cv-saved-views-save">
            <input
              type="text"
              className="cv-saved-views-name"
              placeholder="Name this view…"
              value={name}
              maxLength={60}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') save(); }}
            />
            <button type="button" className="cv-reset-btn" onClick={save} disabled={!trimmedName}>
              Save
            </button>
          </div>
          {(canEditGlobal || canSaveTeam) && (
            <label className="cv-saved-views-shared">
              Save to{' '}
              <select
                aria-label="Save to"
                value={target}
                onChange={(e) => setTarget(e.target.value as SaveTarget)}
              >
                <option value="personal">My views (only me)</option>
                {canSaveTeam && <option value="team">My team (my districts)</option>}
                {canEditGlobal && <option value="global">Shared (everyone)</option>}
              </select>
            </label>
          )}
          <div className="cv-saved-views-hint">
            Saves {currentCount === 0 ? 'no filters' : `${currentCount} active filter${currentCount === 1 ? '' : 's'}`} + the current sort.
            {' '}Use ↻ on a view to overwrite it with these.
          </div>

          {total === 0 ? (
            <div className="cv-saved-views-empty">No saved views yet</div>
          ) : (
            <div className="cv-saved-views-list">
              {personal.length > 0 && (
                <>
                  <div className="cv-saved-views-group">My views</div>
                  <ul>{personal.map((s) => renderRow(s, 'personal', true))}</ul>
                </>
              )}
              {teams.filter((t) => t.views.length > 0).map((t) => (
                <React.Fragment key={t.owner}>
                  <div className="cv-saved-views-group" title={t.audience.join(', ')}>
                    {t.owner === teamOwner
                      ? 'My team views'
                      : `Team views · ${teamLabel ? teamLabel(t) : t.owner}`}
                  </div>
                  <ul>{t.views.map((s) => renderRow(s, teamScope(t.owner), t.editable))}</ul>
                </React.Fragment>
              ))}
              {globalViews.length > 0 && (
                <>
                  <div className="cv-saved-views-group">Shared views</div>
                  <ul>{globalViews.map((s) => renderRow(s, 'global', canEditGlobal))}</ul>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
